import React, {useCallback, useEffect, useState} from 'react';
import I18n from 'app/i18n';
import {isGuest} from 'app/utils';
import {amountWithSign} from 'app/utils/format';
import {shopExchangeController} from 'app/controllers/shop/shopExchangeController';
import {openAuth} from 'app/components/auth/auth';
import PointsExchangeSlider from './PointsExchangeSlider';
import {clamp} from 'app/utils/clamp';
import {toastError} from 'app/components/toasts/liteToast';
import {
    EXCHANGE_MIN_POINTS,
    EXCHANGE_SCALE_MAX,
    bonusPercentFor,
    defaultSelectedPoints,
    formatPointsAmount,
    moneyForPoints,
    nextTier
} from 'app/utils/shopPointsExchange';

interface Props {
    points: string;
}

const PointsExchangeCard = ({points}: Props): React.ReactElement => {
    const pointsAmount = Number(points) || 0;
    const [selectedPoints, setSelectedPoints] = useState(() => defaultSelectedPoints(pointsAmount));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const money = moneyForPoints(selectedPoints);
    const remaining = Math.max(pointsAmount - selectedPoints, 0);
    const upcomingTier = nextTier(selectedPoints);
    const canExchangeSelectedPoints = selectedPoints <= pointsAmount;
    const guest = isGuest();
    const buttonClassName = `button-adaptive shop-exchange__btn ${guest || canExchangeSelectedPoints ? '' : 'button-adaptive_disable'}`;

    useEffect(() => {
        setSelectedPoints((currentPoints: number) => {
            if (currentPoints >= EXCHANGE_MIN_POINTS && currentPoints <= EXCHANGE_SCALE_MAX) {
                return currentPoints;
            }

            return defaultSelectedPoints(pointsAmount);
        });
    }, [pointsAmount]);

    const handleSelect = useCallback((value: number) => {
        setSelectedPoints(clamp(value, EXCHANGE_MIN_POINTS, EXCHANGE_SCALE_MAX));
    }, []);

    const handleExchange = useCallback(() => {
        if (isGuest()) {
            openAuth();
            return;
        }

        if (!canExchangeSelectedPoints) {
            toastError(I18n.t('shop.not_enough_points', {count: selectedPoints - pointsAmount}));
            return;
        }

        setIsSubmitting(true);
        shopExchangeController.exchange(selectedPoints).finally(() => {
            setIsSubmitting(false);
        });
    }, [
        canExchangeSelectedPoints,
        pointsAmount,
        selectedPoints
    ]);

    return <div className="shop-exchange-card">
        <div className="shop-exchange-card__summary">
            <div className="shop-exchange-card__give">
                <span className="shop-exchange-card__label">{I18n.t('shop.exchange.you_give')}</span>
                <span className="shop-exchange-card__value">
                    {I18n.t('egb_points', {amount: formatPointsAmount(selectedPoints)})}
                </span>
                <p className="shop-exchange-card__remaining">
                    {I18n.t('shop.exchange.remaining_before')}
                    <span className="shop-exchange-card__remaining-amount">
                        {formatPointsAmount(remaining)}
                    </span>
                    {` ${I18n.t('points', {count: remaining})}`}
                </p>
            </div>
            <span className="shop-exchange-card__arrow">→</span>
            <div className="shop-exchange-card__receive">
                <span className="shop-exchange-card__label">{I18n.t('shop.exchange.you_receive')}</span>
                <span className="shop-exchange-card__value shop-exchange-card__value_money">
                    {amountWithSign(money)}
                </span>
            </div>
        </div>
        <PointsExchangeSlider
            balance={pointsAmount}
            onChange={handleSelect}
            selectedPoints={selectedPoints}
        />
        <div className="shop-exchange-card__next">
            {upcomingTier
                ? <p
                    className="shop-exchange-card__next-text"
                    dangerouslySetInnerHTML={{
                        __html: I18n.t('shop.exchange.next_tier', {
                            amount: formatPointsAmount(upcomingTier.points - selectedPoints),
                            percent: upcomingTier.bonusPercent
                        })
                    }}
                />
                : <span>
                    {I18n.t('shop.exchange.max_tier_rate', {
                        percent: bonusPercentFor(selectedPoints)
                    })}
                </span>}
        </div>
        <div className="shop-exchange-card__actions">
            <button
                className={buttonClassName}
                disabled={isSubmitting}
                onClick={handleExchange}
                type="button"
            >
                {I18n.t('shop.exchange.exchange_for', {amount: amountWithSign(money)})}
            </button>
            <p className="shop-exchange-card__footnote">
                {I18n.t('shop.exchange.min_irreversible')}
            </p>
        </div>
    </div>;
};

export default PointsExchangeCard;
